/**
 * imprimir.tsx — Documento de impressão/PDF da Ordem de Serviço (paisagem).
 *
 * Estrutura em "folhas" (preview mostra cada página como uma folha A4 paisagem):
 *  - Página 1: logo + cabeçalho + dados do cliente/endereço/vendedor/pedido +
 *    produtos (a "introdução").
 *  - Páginas 2+: ambientes (cômodos com medidas e fotos), N por página.
 * Determinístico (CSS próprio, page-break por folha). Gera PDF via "Salvar como
 * PDF" do navegador. Mostra só o que está preenchido. Gancho de IA p/ depois.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import { formatarData } from '@/lib/data';
import { rotuloTipo } from './shared';
import type { OSAmbiente, OSDetalhe } from './shared';

const MARROM = '#5b4a2f';

// Config por orientação: nº de colunas/ambientes por folha + tamanho da folha.
const CFG = {
  retrato: { cols: 2, porPag: 4, w: '794px', h: '1080px', page: 'A4' },
  paisagem: { cols: 3, porPag: 3, w: '1123px', h: '760px', page: 'A4 landscape' },
} as const;
type Orientacao = keyof typeof CFG;

const v = (s?: string | null) => (s && s.trim() ? s.trim() : null);

function enderecoLinha(os: OSDetalhe): string | null {
  const rua = [v(os.endereco), v(os.numero)].filter(Boolean).join(', ');
  const linha2 = [v(os.bairro), v(os.cidade), v(os.uf)].filter(Boolean).join(' · ');
  const partes = [
    rua || null,
    v(os.complemento),
    linha2 || null,
    v(os.cep) ? `CEP ${os.cep}` : null,
  ].filter(Boolean);
  return partes.length ? partes.join(' — ') : null;
}

// Formata telefone E.164 p/ exibição: BR vira "+55 (22) 99999-8888"; demais "+DDI número".
function formatarTelefone(value?: string | null): string | null {
  const d = (value ?? '').replace(/\D/g, '');
  if (!d) return null;
  if (d.startsWith('55') && d.length >= 12) {
    const n = d.slice(2);
    const ddd = n.slice(0, 2);
    const resto = n.slice(2);
    const cel = resto.length > 8;
    const meio = cel ? resto.slice(0, 5) : resto.slice(0, 4);
    const fim = cel ? resto.slice(5) : resto.slice(4);
    return `+55 (${ddd}) ${meio}-${fim}`;
  }
  return `+${d}`;
}

function chunk<T>(arr: T[], n: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
}

export function ImprimirOSPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [os, setOs] = useState<OSDetalhe | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [orientacao, setOrientacao] = useState<Orientacao>('retrato');

  useEffect(() => {
    api
      // Porta própria do documento (`ordens-servico.impressao.ver`), e não o
      // detalhe da tela: é o que permite dar a impressão a quem não edita nada
      // — o instalador leva o papel para o campo sem entrar no formulário.
      .get<OSDetalhe>(`/ordens-servico/${id}/impressao`)
      .then(setOs)
      .catch((e) =>
        setErro(e instanceof ApiError ? e.message : 'Erro ao carregar a OS.'),
      );
  }, [id]);

  if (erro) return <div className="p-4 text-danger">{erro}</div>;
  if (!os) {
    return (
      <div className="p-4 text-secondary">
        <span className="spinner-border spinner-border-sm me-2" /> Carregando…
      </div>
    );
  }

  const itens = (os.itens ?? []).filter((it) => v(it.descricao));
  const ambientes = (os.ambientes ?? []).filter(
    (a) =>
      v(a.descricao) ||
      (a.medidas ?? []).length > 0 ||
      (a.fotos ?? []).length > 0,
  );
  const endereco = enderecoLinha(os);
  const cfg = CFG[orientacao];
  const paginasAmb = chunk(ambientes, cfg.porPag);
  const totalPaginas = 1 + paginasAmb.length;

  const dados: { rotulo: string; valor: string; icone?: string }[] = [
    { rotulo: 'Cliente', valor: os.cliente_nome },
    v(os.cliente_telefone)
      ? {
          rotulo: 'Telefone',
          valor: formatarTelefone(os.cliente_telefone)!,
          icone: 'bi-telephone',
        }
      : null,
    v(os.cliente_whatsapp)
      ? {
          rotulo: 'WhatsApp',
          valor: formatarTelefone(os.cliente_whatsapp)!,
          icone: 'bi-whatsapp',
        }
      : null,
    v(os.cliente_email)
      ? { rotulo: 'E-mail', valor: os.cliente_email!, icone: 'bi-envelope' }
      : null,
    endereco ? { rotulo: 'Endereço', valor: endereco } : null,
    v(os.referencia)
      ? { rotulo: 'Ponto de referência', valor: os.referencia! }
      : null,
    v(os.vendedor) ? { rotulo: 'Vendedor', valor: os.vendedor! } : null,
    v(os.responsavel)
      ? { rotulo: 'Responsável', valor: os.responsavel! }
      : null,
    v(os.arquiteto) ? { rotulo: 'Arquiteto', valor: os.arquiteto! } : null,
    v(os.pedido_venda)
      ? { rotulo: 'Pedido de venda', valor: os.pedido_venda! }
      : null,
  ].filter(Boolean) as { rotulo: string; valor: string; icone?: string }[];

  const cabecalho = (intro: boolean) => (
    <div className="doc-head">
      <div className="d-flex align-items-center gap-2">
        <img src="/assets/img/casaa-logo-bege.png" alt="Casa A" className="doc-logo" />
        {intro && (
          <div>
            <div className="doc-marca">Casa A</div>
            <div className="doc-sub">Cortinas e persianas · Búzios / Trancoso</div>
          </div>
        )}
      </div>
      <div className="text-end">
        <div className="doc-titulo">Ordem de Serviço</div>
        <div className="doc-codigo">{os.codigo}</div>
        <div className="doc-sub">
          {rotuloTipo(os.tipo)}
          {os.dt_abertura ? ` · ${formatarData(os.dt_abertura)}` : ''}
        </div>
      </div>
    </div>
  );

  return (
    <div className="doc-os-wrap">
      <style>{cssDoc(cfg)}</style>

      {/* Barra de ações (não sai na impressão) */}
      <div className="no-print doc-toolbar">
        <button
          className="btn btn-link"
          onClick={() => navigate(`/ordens-servico/${id}`)}
        >
          <i className="bi bi-arrow-left me-1"></i> Voltar à OS
        </button>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary"
            onClick={() =>
              setOrientacao((o) => (o === 'retrato' ? 'paisagem' : 'retrato'))
            }
            title="Alternar retrato / paisagem"
          >
            <i className="bi bi-arrow-repeat me-1"></i>
            {orientacao === 'retrato' ? 'Retrato' : 'Paisagem'}
          </button>
          <button
            className="btn btn-outline-secondary"
            disabled
            title="Em breve: reorganizar o layout com IA"
          >
            <i className="bi bi-stars me-1"></i> Reorganizar com IA
          </button>
          <button className="btn btn-theme" onClick={() => window.print()}>
            <i className="bi bi-printer me-1"></i> Imprimir / Salvar PDF
          </button>
        </div>
      </div>

      {/* PÁGINA 1 — introdução (cliente + produtos) */}
      <div className="folha">
        {cabecalho(true)}

        <section className="doc-dados">
          {dados.map((d) => (
            <div className="doc-dado" key={d.rotulo}>
              <span className="doc-rot">
                {d.icone && <i className={`bi ${d.icone} me-1`}></i>}
                {d.rotulo}
              </span>
              <span className="doc-val">{d.valor}</span>
            </div>
          ))}
        </section>

        {itens.length > 0 && (
          <section className="mt-3">
            <h2 className="doc-secao">Produtos</h2>
            <table className="doc-tabela">
              <thead>
                <tr>
                  <th style={{ width: '60px' }}>Qtd</th>
                  <th>Descrição</th>
                  <th>Observação</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((it, i) => (
                  <tr key={i}>
                    <td>{it.quantidade}</td>
                    <td>{it.descricao}</td>
                    <td>{v(it.observacao) ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {v(os.observacoes) && (
          <section className="mt-3">
            <h2 className="doc-secao">Observações</h2>
            <div style={{ whiteSpace: 'pre-wrap' }}>{os.observacoes}</div>
          </section>
        )}

        {os.cliente_obs_pdf && v(os.cliente_obs) && (
          <section className="mt-3">
            <h2 className="doc-secao">Anotações do cliente</h2>
            <div style={{ whiteSpace: 'pre-wrap' }}>{os.cliente_obs}</div>
          </section>
        )}
        <div className="folha-num">1 / {totalPaginas}</div>
      </div>

      {/* PÁGINAS 2+ — ambientes */}
      {paginasAmb.map((grupo, gi) => (
        <div className="folha" key={gi}>
          {cabecalho(false)}
          <h2 className="doc-secao">
            Ambientes{paginasAmb.length > 1 ? ` (${gi + 1}/${paginasAmb.length})` : ''}
          </h2>
          <div className="amb-grid">
            {grupo.map((a, ai) => (
              <Ambiente key={ai} a={a} indice={gi * cfg.porPag + ai} />
            ))}
          </div>
          <div className="folha-num">
            {gi + 2} / {totalPaginas}
          </div>
        </div>
      ))}
    </div>
  );
}

function Ambiente({ a, indice }: { a: OSAmbiente; indice: number }) {
  const fotosLegado = a.fotos ?? [];
  return (
    <div className="amb">
      <h3 className="amb-titulo">{v(a.descricao) ?? `Ambiente ${indice + 1}`}</h3>
      {(a.medidas ?? []).map((m, mi) => (
        <div key={mi} className="med">
          <div className="med-campos">
            {v(m.abertura) && (
              <span>
                <b>Abertura:</b> {m.abertura}
              </span>
            )}
            {v(m.medida) && (
              <span>
                <b>Medida:</b> {m.medida}
              </span>
            )}
            {v(m.modelo) && (
              <span>
                <b>Produto:</b> {m.modelo}
              </span>
            )}
            {v(m.fixacao) && (
              <span>
                <b>Fixação:</b> {m.fixacao}
              </span>
            )}
          </div>
          {v(m.obs) && <div className="med-obs">{m.obs}</div>}
          {(m.fotos ?? []).length > 0 && (
            <div className="med-fotos">
              {m.fotos.map((f, fi) => (
                <img key={fi} src={`/uploads/${f.arquivo}`} alt="" />
              ))}
            </div>
          )}
        </div>
      ))}
      {fotosLegado.length > 0 && (
        <div className="med-fotos">
          {fotosLegado.map((f, fi) => (
            <img key={fi} src={`/uploads/${f.arquivo}`} alt="" />
          ))}
        </div>
      )}
      {v(a.obs) && (
        <div className="med-obs">
          <b>Obs. do ambiente:</b> {a.obs}
        </div>
      )}
    </div>
  );
}

function cssDoc(cfg: (typeof CFG)[Orientacao]) {
  return `
.doc-os-wrap { background: #e9e6df; min-height: 100vh; padding-bottom: 24px; }
.doc-toolbar {
  position: sticky; top: 0; z-index: 5; display: flex; align-items: center;
  justify-content: space-between; gap: 12px; padding: 12px 16px;
  background: #fff; border-bottom: 1px solid #e5e7eb; margin-bottom: 24px;
}
/* folha A4 no preview (tamanho conforme orientação) */
.folha {
  background: #fff; color: #1f2937; width: ${cfg.w}; min-height: ${cfg.h};
  margin: 0 auto 24px; padding: 32px 40px; box-shadow: 0 2px 12px rgba(0,0,0,.12);
  font-size: 13px; line-height: 1.45; display: flex; flex-direction: column;
}
.folha-num { margin-top: auto; padding-top: 10px; text-align: right; color: #9ca3af; font-size: 11px; }
.doc-head {
  display: flex; justify-content: space-between; align-items: flex-start;
  border-bottom: 3px solid ${MARROM}; padding-bottom: 12px; margin-bottom: 16px;
}
.doc-logo { width: 54px; height: 54px; border-radius: 8px; }
.doc-marca { font-size: 22px; font-weight: 800; color: ${MARROM}; }
.doc-sub { font-size: 11px; color: #6b7280; }
.doc-titulo { font-size: 15px; font-weight: 700; color: ${MARROM}; }
.doc-codigo { font-size: 20px; font-weight: 800; color: #111827; }

.doc-dados { display: flex; flex-direction: column; gap: 4px; max-width: 70%; }
.doc-dado { display: flex; gap: 8px; }
.doc-rot { min-width: 130px; color: #6b7280; font-weight: 600; }
.doc-val { color: #111827; }

.doc-secao {
  font-size: 11px; text-transform: uppercase; letter-spacing: .6px;
  color: ${MARROM}; font-weight: 700; border-bottom: 1px solid #e5e7eb;
  padding-bottom: 4px; margin: 0 0 8px;
}
.doc-tabela { width: 100%; border-collapse: collapse; }
.doc-tabela th, .doc-tabela td {
  text-align: left; padding: 5px 8px; border-bottom: 1px solid #e5e7eb; vertical-align: top;
}
.doc-tabela th { font-size: 11px; text-transform: uppercase; color: #6b7280; }

.amb-grid { display: grid; grid-template-columns: repeat(${cfg.cols}, 1fr); gap: 16px; }
.amb { border: 1px solid #e5e7eb; border-top: 3px solid ${MARROM}; border-radius: 8px; padding: 10px 12px; }
.amb-titulo { font-size: 14px; font-weight: 700; color: #111827; margin: 0 0 6px; }
.med { padding: 6px 0; border-top: 1px dashed #e5e7eb; }
.med:first-of-type { border-top: 0; }
.med-campos { display: flex; flex-direction: column; gap: 1px; }
.med-campos b { color: #6b7280; font-weight: 600; }
.med-obs { color: #4b5563; font-style: italic; margin-top: 3px; font-size: 12px; }
.med-fotos { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
.med-fotos img { width: 100%; max-height: 150px; object-fit: cover; border-radius: 6px; border: 1px solid #e5e7eb; }

@media print {
  .no-print { display: none !important; }
  .doc-os-wrap { background: #fff; padding: 0; }
  .folha {
    width: auto; min-height: auto; box-shadow: none; margin: 0; padding: 0;
    page-break-after: always;
  }
  .folha:last-child { page-break-after: auto; }
  .amb, .med, .med-fotos img { page-break-inside: avoid; }
  @page { size: ${cfg.page}; margin: 12mm; }
}
`;
}
